import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import TextInput from 'components/Common/Form/Input/TextInput'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { getData as getCountryData } from 'country-list'
import Select from 'components/Common/Form/Input/Select'
import BUSINESS_TRAVELLER_BUSINESS_INDUSTRIES_OPTIONS from 'businessTraveller/constants/businessTravellerBusinessIndustries'
import BUSINESS_TRAVELLER_ESTIMATED_TRAVEL_SPEND_PER_ANNUM_OPTIONS from 'businessTraveller/constants/businessTravellerEstimatedTravelSpendPerAnnum'
import Hr from 'components/Common/Hr'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import DateInput from 'components/Common/Form/Input/DateInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useMutation } from '@tanstack/react-query'
import registerBusiness from 'businessTraveller/api/registerBusiness'
import requestDemo from 'businessTraveller/api/requestPostRequestDemo'
import { RequestDemoData } from 'businessTraveller/components/demo-form/RequestDemoForm'
import { formToObject } from 'lib/forms/formToObject'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { syncUserDetails } from 'actions/AuthActions'
import { pushWithRegion } from 'actions/NavigationActions'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE, BUSINESS_TRAVELLER_ROOT_ROUTE } from 'components/App/Routes/routeConstants'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_ACCOUNT_DETAILS,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
  BusinessTravellerBusinessOnboardingStateContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import LayoutContainer from 'components/Common/LayoutContainer'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { BUSINESS_TRAVELLER_BUSINESS_EMPLOYEE_COUNT_OPTIONS, isSmallTeam } from 'businessTraveller/constants/businessTravellerBusinessEmployeeCount'
import { createDatadogLoggerWithContext } from 'services/datadogRum'
import { getSydneyTimestamp } from 'lib/datetime/dateUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useUserUpdateMutation from 'businessTraveller/hooks/useUserUpdateMutation'
import { DATE_OF_BIRTH } from 'constants/dates'
import { COMPANION_TITLES } from 'constants/companions'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useAbnValidation from 'businessTraveller/hooks/useAbnValidation'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const countryNames = getCountryData().map(country => country.name).sort()

const AccountDetailsForm = styled.form`
  position: relative;
`

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const stepStatuses: App.BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'complete',
  account_details: 'active',
  create_password: 'pending',
}

interface Props {
  steps: App.BusinessTraveller.BusinessSignupSteps
}

function mapSignupFormToDemoRequest(formData: App.BusinessTraveller.BusinessSignupFormData, userEmail: string): RequestDemoData {
  return {
    fullName: `${formData.adminDetails.firstName} ${formData.adminDetails.lastName}`,
    mobileNumber: `${formData.businessDetails.phone.prefix} ${formData.businessDetails.phone.phone}`,
    positionInCompany: 'Decision Maker',
    workEmail: userEmail,
    companyName: formData.businessDetails.name,
    businessLocation: formData.businessDetails.country,
    roleInTravelProgram: 'Decision Maker',
    companyTravelSize: formData.businessDetails.numberOfEmployees,
    currentTravelSolution: 'Other',
    message: 'Request demo via signup',
  }
}

function BusinessTravellerSignupAccountDetails(props: Props) {
  const { steps } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)
  const [demoRequested, setDemoRequested] = useState<boolean>(false)
  const [showDemoRecommendation, setShowDemoRecommendation] = useState<boolean>(false)
  const numberOfEmployeesSelectRef = useRef<HTMLSelectElement>(null)

  const dispatch = useAppDispatch()
  const { isApp } = useWhiteLabelAppConfig()
  const { businessName, adminEmail, accountDetails: prevAccountDetails } = useContext(BusinessTravellerBusinessOnboardingStateContext)
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)

  const {
    abn,
    isValid: isAbnValid,
    handleAbnChange,
    errorMessage: abnErrorMessage,
    successMessage: abnSuccessMessage,
    getInvalidMessage: getAbnInvalidMessage,
  } = useAbnValidation(prevAccountDetails?.businessDetails.businessIdentifier)

  const business = useAppSelector((state) => state.businessTraveller.currentBusinessAccount?.business)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const currentRegionCode = useAppSelector((state) => state.geo.currentRegionCode)
  const customerId = useAppSelector((state) => state.auth.account.memberId)
  const loggedInEmail = useAppSelector((state) => state.auth.account.email) || adminEmail
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const logWithContext = createDatadogLoggerWithContext({
    businessName,
    customerId,
  })

  const demoRequestMutation = useMutation({
    mutationFn: requestDemo,
  })

  // For the link-account branch (existing LE user), we submit directly:
  // update profile details + register business. No password step follows.
  const userUpdateMutation = useUserUpdateMutation({})

  const businessRegistrationMutation = useMutation({
    mutationFn: registerBusiness,
    onSuccess: (_, businessData) => {
      dispatch(fetchAccountBusiness())

      dispatch(syncUserDetails({
        title: businessData.adminDetails.title,
        givenName: businessData.adminDetails.firstName,
        surname: businessData.adminDetails.lastName,
        dob: businessData.adminDetails.dob,
        postcode: businessData.adminDetails.postcode,
        phone: businessData.businessDetails.phone.phone,
        phonePrefix: businessData.businessDetails.phone.prefix,
      }))

      if (businessData.extraBusinessDetails?.demoRequested) {
        const demoRequestData = mapSignupFormToDemoRequest(businessData, loggedInEmail)
        demoRequestMutation.mutate(demoRequestData)
      }

      setIsFormSubmitting(false)
    },
    onError: (error: App.ApiCallFailure) => {
      logWithContext('Error submitting business', 'error', { errorMessage: error.message, error: JSON.stringify(error), logTimestamp: getSydneyTimestamp() })
      showSnackbar(error.message, 'critical', { heading: 'Error submitting business' })
      setIsFormSubmitting(false)
    },
  })

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    if (!isAbnValid) {
      return
    }
    const formData = formToObject<App.BusinessTraveller.BusinessSignupFormData>(e.currentTarget)

    if (isLoggedIn && !isDedicatedTenantRegistrationEnabled) {
      // Existing LE user path — already logged in, submit now.
      setIsFormSubmitting(true)
      userUpdateMutation.mutate({
        title: formData.adminDetails.title,
        givenName: formData.adminDetails.firstName,
        surname: formData.adminDetails.lastName,
        dob: formData.adminDetails.dob,
        postcode: formData.adminDetails.postcode,
        phone: formData.businessDetails.phone.phone,
        phonePrefix: formData.businessDetails.phone.prefix,
      }, {
        onSuccess: () => {
          businessRegistrationMutation.mutate(formData)
        },
        onError: (error: App.ApiCallFailure) => {
          showSnackbar(error.message, 'critical', { heading: 'Error updating profile' })
          setIsFormSubmitting(false)
        },
      })
      return
    }

    // New LE user path — stash the form data and advance to password step.
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_ACCOUNT_DETAILS,
      accountDetails: formData,
    })
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
      step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.CREATE_PASSWORD,
    })
  }, [accountSetupDispatch, businessRegistrationMutation, isAbnValid, isDedicatedTenantRegistrationEnabled, isLoggedIn, userUpdateMutation])

  const handleNumberOfEmployeesChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setShowDemoRecommendation(!isSmallTeam(e.target.value))
  }, [])

  useEffect(() => {
    if (numberOfEmployeesSelectRef.current?.value && !isSmallTeam(numberOfEmployeesSelectRef.current.value)) {
      setShowDemoRecommendation(true)
    }
  }, [])

  useEffect(() => {
    if (business) {
      dispatch(pushWithRegion(isApp ? BUSINESS_TRAVELLER_ROOT_ROUTE : BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business])

  return (
    <SignupContainer>
      <AccountDetailsForm onSubmit={handleSubmission}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={20}>
          <Heading variant="heading3">Complete your account details</Heading>
          <BusinessTravellerStepper steps={steps} stepStatuses={stepStatuses} />
          <Group direction="vertical" gap={20}>
            <Group direction="vertical">
              <Heading variant="heading6">Business details</Heading>
              <BodyText variant="medium">Tell us about your business</BodyText>
            </Group>
            <InputGrid>
              <TextInput
                label="Business name"
                defaultValue={prevAccountDetails?.businessDetails.name ?? businessName}
                maxLength={100}
                minLength={2}
                name="businessDetails.name"
                placeholder="Enter your business name"
                required
                requiredErrorMessage="Please enter a business name"
              />
              <TextInput
                label="ABN"
                value={abn}
                onChange={handleAbnChange}
                minLength={11}
                name="businessDetails.businessIdentifier"
                pattern="^(\d *?){11}$"
                getInvalidMessage={getAbnInvalidMessage}
                manualError={!!abnErrorMessage}
                invalidErrorMessage={abnErrorMessage}
                successMessage={abnSuccessMessage}
                placeholder="Enter your ABN"
                required
                requiredErrorMessage="Please enter your ABN"
              />
              <HiddenInput name="businessDetails.businessIdentifierType" value="australian_business_number" />
              <Select
                label="Country"
                name="businessDetails.country"
                placeholder="e.g. Australia"
                defaultValue={prevAccountDetails?.businessDetails.country ?? 'Australia'}
                required
                requiredErrorMessage="Please select a country"
              >
                {countryNames.map(
                  (countryName) =>
                    <option key={countryName} value={countryName}>{countryName}</option>,
                )}
              </Select>
              <Select
                label="Industry"
                name="businessDetails.industry"
                placeholder="e.g. Healthcare"
                defaultValue={prevAccountDetails?.businessDetails.industry}
                required
                requiredErrorMessage="Please select an industry"
              >
                {BUSINESS_TRAVELLER_BUSINESS_INDUSTRIES_OPTIONS.map(
                  ([key, label]) =>
                    <option key={key} value={key}>{label}</option>,
                )}
              </Select>
              <Group direction="vertical">
                <Select
                  ref={numberOfEmployeesSelectRef}
                  label="Number of employees who travel for work"
                  name="businessDetails.numberOfEmployees"
                  placeholder="e.g. 1 - 5"
                  defaultValue={prevAccountDetails?.businessDetails.numberOfEmployees}
                  required
                  requiredErrorMessage="Please select your number of employees who travel for work"
                  onChange={handleNumberOfEmployeesChange}
                >
                  {BUSINESS_TRAVELLER_BUSINESS_EMPLOYEE_COUNT_OPTIONS.map(
                    ([key, label]) =>
                      <option key={key} value={key}>{label}</option>,
                  )}
                </Select>
                {showDemoRecommendation && (
                  <BodyText variant="small" colour="primary" weight="bold">
                    For this many travelling employees, we recommend requesting a demo.
                  </BodyText>
                )}
              </Group>
              <Select
                label="Estimated spend on business travel per annum"
                name="extraBusinessDetails.estimatedTravelSpendPerAnnum"
                placeholder="e.g. Up to $10k"
                defaultValue={prevAccountDetails?.extraBusinessDetails?.estimatedTravelSpendPerAnnum}
                required
                requiredErrorMessage="Please select your estimated travel spend"
              >
                {BUSINESS_TRAVELLER_ESTIMATED_TRAVEL_SPEND_PER_ANNUM_OPTIONS.map(
                  ([key, label]) =>
                    <option key={key} value={key}>{label}</option>,
                )}
              </Select>
            </InputGrid>
          </Group>
          <Hr />
          <Group direction="vertical" gap={20}>
            <Group direction="vertical">
              <Heading variant="heading6">User account details</Heading>
              <BodyText variant="medium">Provide your user account details</BodyText>
            </Group>
            <MessageBanner
              kind="warning"
              heading="Personal details must match passport"
              description="This information will be used for all your business travel bookings."
              density="compact"
            />
            <InputGrid>
              <Select
                name="adminDetails.title"
                label="Title"
                placeholder="Select"
                defaultValue={prevAccountDetails?.adminDetails.title}
                required
                requiredErrorMessage="Please select a title"
              >
                {COMPANION_TITLES.map(title =>
                  <option key={title} value={title}>{title}</option>,
                )}
              </Select>
              <TextInput
                autoComplete="given-name"
                label="First name"
                name="adminDetails.firstName"
                placeholder="First name"
                defaultValue={prevAccountDetails?.adminDetails.firstName}
                pattern="^[^\.\$\%\&\(\)\*!,@#\d]*$"
                required
                requiredErrorMessage="Please enter first name"
              />
              <TextInput
                autoComplete="family-name"
                label="Last name"
                name="adminDetails.lastName"
                placeholder="Last name"
                defaultValue={prevAccountDetails?.adminDetails.lastName}
                pattern="^[^\.\$\%\&\(\)\*!,@#\d]*$"
                required
                requiredErrorMessage="Please enter last name"
              />
              <DateInput
                name="adminDetails.dob"
                label="Date of Birth"
                defaultValue={prevAccountDetails?.adminDetails.dob}
                min={DATE_OF_BIRTH.minValue}
                max={DATE_OF_BIRTH.maxValue}
                required
              />
              <TextInput
                name="adminDetails.postcode"
                label="Postcode"
                placeholder="e.g. 2000"
                defaultValue={prevAccountDetails?.adminDetails.postcode}
                pattern="^[^\.\$\%\&\(\)\*!,@#]*$"
                invalidErrorMessage="Please enter a valid postcode"
                minLength={3}
                maxLength={15}
                required
                requiredErrorMessage="Please enter a postcode"
              />
              <PhoneInput
                defaultPhoneNumber={prevAccountDetails?.businessDetails.phone.phone}
                defaultPhonePrefix={prevAccountDetails?.businessDetails.phone.prefix ?? '+61'}
                label="Phone number"
                name="businessDetails.phone"
                placeholder="e.g. 0400 000 000"
                prefixCountry={currentRegionCode}
                required
                requiredErrorMessage="Number provided is incomplete. Please enter a 10-digit phone number"
              />
            </InputGrid>
          </Group>
          <Hr />
          <Group direction="vertical" gap={20}>
            <TextInput
              autoComplete="referral-code"
              label="Referral code"
              name="businessDetails.referralCode"
              placeholder="Enter referral code"
              optionalIndicatorEnabled
            />
            <TextInput
              autoComplete="referral-email"
              label="Referrer's email address"
              name="businessDetails.referralEmail"
              placeholder="Enter referrer's email"
              optionalIndicatorEnabled
            />
          </Group>
          <Group
            direction="vertical"
            tabletDirection="horizontal"
            gap={16}
            horizontalAlign="start"
            tabletHorizontalAlign="end"
            verticalAlign="center"
          >
            <CheckboxInput
              checked={demoRequested}
              name="extraBusinessDetails.demoRequested::boolean"
              onChange={(e) => setDemoRequested(e.target.checked)}
            >
              Request a demo
            </CheckboxInput>
            <TextButton
              fit="mobile-full-width"
              kind="primary"
              shape="square"
              size="medium"
              type="submit"
              disabled={isFormSubmitting || !isAbnValid}
            >
              {isLoggedIn ? 'Create account' : 'Continue'}
            </TextButton>
          </Group>
        </Group>
      </AccountDetailsForm>
    </SignupContainer>
  )
}

export default BusinessTravellerSignupAccountDetails
